"use client";
import { useState, useRef, useEffect } from "react";
import { LogOut, ChevronUp, CreditCard } from "lucide-react";
import { AppTab } from "@/components/data";
import { useClarityAuth } from "@/lib/auth/use-clarity-auth";
import cn from "@/lib/cn";
import ClarityLogomark from "@/components/ClarityLogomark";

interface Props {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  expanded: boolean;
}

type NavItem = {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
    size?: number;
  }>;
  tab: AppTab;
  label: string;
};

function getUserDisplayName(user: {
  name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  nickname?: string | null;
  email?: string | null;
}) {
  const givenFamilyName = [user.given_name, user.family_name]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ");

  return (
    givenFamilyName ||
    user.name?.trim() ||
    user.nickname?.trim() ||
    user.email?.trim() ||
    "Signed in"
  );
}

function getUserInitials(user: {
  name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  nickname?: string | null;
  email?: string | null;
}) {
  const displayName = getUserDisplayName(user);
  const parts = displayName
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return "CU";
}

/* ── Nav items ── */

const NAV_ITEMS: NavItem[] = [
  { icon: CreditCard, tab: "tasks14" as AppTab, label: "Card Collection" },
];

/* ── Clarity logomark (three red petals) ── */

/* ── Full ClarityOS logo (logomark + wordmark + OS) ── */
function ClarityFullLogo() {
  return (
    <svg
      width="130"
      height="26"
      viewBox="0 41 1265 255"
      fill="none"
      className="shrink-0"
    >
      {/* Logomark */}
      <path
        d="M119.506 210.703L130.566 221.793C147.666 238.939 170.854 248.568 195.031 248.568C195.031 206.75 161.222 172.839 119.506 172.839C77.79 172.839 43.9805 206.739 43.9805 248.568C68.1574 248.568 91.346 238.939 108.446 221.793L119.506 210.703Z"
        fill="#FF2D00"
      />
      <path
        d="M70.464 125.517L55.3514 129.581C32.003 135.846 12.0885 151.17 0 172.159C36.1257 193.068 82.313 180.657 103.166 144.444C124.019 108.221 111.641 61.909 75.525 41C63.437 61.9991 60.1628 86.9418 66.4217 110.363L70.474 125.517H70.464Z"
        fill="#FF2D00"
      />
      <path
        d="M168.57 125.516L172.623 110.363C178.882 86.9413 175.598 61.9986 163.509 40.9995C127.383 61.9085 115.015 108.221 135.868 144.444C156.721 180.667 202.909 193.068 239.035 172.159C226.946 151.16 207.032 135.846 183.683 129.57L168.57 125.506V125.516Z"
        fill="#FF2D00"
      />
      {/* Wordmark: Clarity */}
      <path
        d="M290.42 141.282C290.42 90.8064 322.413 54.7135 376.048 54.7135C421.447 54.7135 450.854 80.4269 455.796 115.329H426.158C420.988 92.9183 403.579 79.0056 376.287 79.0056C339.822 79.0056 318.889 103.538 318.889 141.272C318.889 179.007 339.822 203.539 376.287 203.539C403.579 203.539 420.978 189.626 426.158 167.216H455.796C450.854 202.128 421.447 227.831 376.048 227.831C322.413 227.831 290.42 191.738 290.42 141.262V141.282Z"
        fill="black"
      />
      <path
        d="M470.131 58.7252H495.776V223.836H470.131V58.7252Z"
        fill="black"
      />
      <path
        d="M511.058 168.874C511.058 136.565 531.521 111.081 565.63 111.081C580.214 111.081 593.391 117.687 599.979 128.767V113.904H625.624V223.824H599.979V208.96C593.391 220.051 578.338 226.647 564.932 226.647C529.645 226.647 511.058 201.173 511.058 168.854V168.874ZM601.157 168.874C601.157 150.007 590.576 133.962 568.695 133.962C548.701 133.962 536.702 148.826 536.702 168.874C536.702 188.922 548.701 203.786 568.695 203.786C590.576 203.786 601.157 187.751 601.157 168.874Z"
        fill="black"
      />
      <path
        d="M649.133 113.915H674.777V135.144C678.78 119.58 691.947 112.504 703.945 112.504C707.948 112.504 711.243 112.744 714.057 113.445V137.036C710.055 136.566 707.938 136.566 703.945 136.566C685.828 136.566 674.777 150.248 674.777 175.251V223.845H649.133V113.925V113.915Z"
        fill="black"
      />
      <path
        d="M719.722 79.0151C719.722 68.4055 727.488 59.9077 738.778 59.9077C750.068 59.9077 757.834 68.3954 757.834 79.0151C757.834 89.6348 750.068 98.1225 738.778 98.1225C727.488 98.1225 719.722 89.6348 719.722 79.0151ZM725.841 113.927H751.486V223.847H725.841V113.927Z"
        fill="black"
      />
      <path
        d="M769.348 80.6665H794.992V113.927H820.167V135.386H794.992V180.678C794.992 199.785 800.872 202.137 810.984 202.137C814.986 202.137 821.335 201.667 823.921 201.196V223.837C819.918 224.548 812.391 225.248 804.864 225.248C792.397 225.248 769.348 223.597 769.348 186.563V80.6665Z"
        fill="black"
      />
      <path
        d="M938.248 113.915V224.306C938.248 241.522 933.077 254.493 923.893 263.462C914.949 271.719 902.252 276.203 886.729 276.203C871.207 276.203 857.791 272.19 849.086 264.883C841.32 258.047 837.088 248.368 836.858 236.347H862.732C863.91 248.378 869.79 254.984 886.26 254.984C901.553 254.984 912.603 245.785 912.603 222.434V208.522C905.785 220.312 893.547 226.688 877.316 226.688C850.264 226.688 836.858 207.35 836.858 180.926V113.935H862.502V175.501C862.502 191.776 867.913 204.037 886.26 204.037C904.607 204.037 912.603 189.644 912.603 170.777V113.935H938.248V113.915Z"
        fill="black"
      />
      {/* OS */}
      <path
        d="M1046.63 229.052C992.52 229.052 960.342 189.247 960.342 141.576C960.342 93.9052 992.52 54.1001 1046.63 54.1001C1100.73 54.1001 1132.91 93.9052 1132.91 141.576C1132.91 189.247 1100.73 229.052 1046.63 229.052ZM1046.63 204.502C1083.57 204.502 1104.31 176.137 1104.07 141.576C1104.31 107.015 1083.57 78.6505 1046.63 78.6505C1009.68 78.6505 988.945 107.015 989.183 141.576C988.945 176.137 1009.68 204.502 1046.63 204.502ZM1200.52 229.052C1161.9 229.052 1139.74 207.838 1139.74 170.417H1167.15C1167.15 192.107 1179.54 205.217 1200.28 205.217C1218.39 205.217 1228.4 196.874 1228.4 181.858C1228.4 169.225 1222.44 162.79 1203.61 155.162L1176.44 144.198C1155.47 136.094 1144.5 121.078 1144.5 99.8641C1144.5 72.6917 1166.43 54.1001 1197.89 54.1001C1231.03 54.1001 1252.95 74.8369 1252.95 106.3H1225.54C1225.54 87.9463 1215.53 77.4588 1196.94 77.4588C1181.21 77.4588 1172.15 85.5628 1172.15 98.1956C1172.15 109.637 1180.26 117.264 1199.8 125.13L1224.35 135.14C1245.33 143.721 1255.81 158.499 1255.81 181.143C1255.81 209.745 1233.65 229.052 1200.52 229.052Z"
        fill="black"
      />
    </svg>
  );
}

/* ── QR Code placeholder SVG ── */
function QRCodeMini({ size = 48 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      className="shrink-0"
    >
      <rect width="48" height="48" rx="4" fill="white" />
      {/* Top-left finder */}
      <rect
        x="4"
        y="4"
        width="14"
        height="14"
        rx="2"
        stroke="#1A1A1A"
        strokeWidth="2.5"
        fill="none"
      />
      <rect x="7.5" y="7.5" width="7" height="7" rx="1" fill="#1A1A1A" />
      {/* Top-right finder */}
      <rect
        x="30"
        y="4"
        width="14"
        height="14"
        rx="2"
        stroke="#1A1A1A"
        strokeWidth="2.5"
        fill="none"
      />
      <rect x="33.5" y="7.5" width="7" height="7" rx="1" fill="#1A1A1A" />
      {/* Bottom-left finder */}
      <rect
        x="4"
        y="30"
        width="14"
        height="14"
        rx="2"
        stroke="#1A1A1A"
        strokeWidth="2.5"
        fill="none"
      />
      <rect x="7.5" y="33.5" width="7" height="7" rx="1" fill="#1A1A1A" />
      {/* Data dots */}
      <rect x="22" y="4" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="22" y="10" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="26" y="7" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="4" y="22" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="10" y="22" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="7" y="25" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="22" y="22" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="28" y="22" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="34" y="22" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="40" y="22" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="22" y="28" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="25" y="31" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="31" y="28" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="37" y="28" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="22" y="37" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="28" y="34" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="34" y="37" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="40" y="34" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="37" y="40" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="31" y="40" width="3" height="3" rx="0.5" fill="#1A1A1A" />
      <rect x="40" y="40" width="4" height="4" rx="1" fill="#1A1A1A" />
    </svg>
  );
}

/* ── Nav button ── */
function NavButton({
  item,
  isActive,
  onClick,
  expanded,
}: {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  expanded: boolean;
}) {
  const Icon = item.icon;
  const [hovered, setHovered] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const getTooltipPos = () => {
    if (!btnRef.current) return { top: 0, left: 0 };
    const rect = btnRef.current.getBoundingClientRect();
    return { top: rect.top + rect.height / 2, left: rect.right + 10 };
  };

  return (
    <div className={cn("relative", expanded ? "w-full" : "w-auto")}>
      <button
        ref={btnRef}
        aria-label={item.label}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          "flex h-10 shrink-0 cursor-pointer items-center gap-3 rounded-full border-0 transition-[background,width,height] duration-200 ease-out",
          expanded
            ? "w-full justify-start px-[14px]"
            : "w-10 justify-center px-0",
          isActive ? "bg-[#1A1A1A]" : "bg-transparent",
          !isActive && hovered && "bg-[var(--surface-muted)]",
        )}
      >
        <Icon
          className={cn(
            "size-5 min-w-5 transition-colors duration-150 ease-out",
            isActive
              ? "text-[var(--content-inverted)]"
              : "text-[var(--content-primary)]",
          )}
          strokeWidth={1.7}
        />
        {expanded && (
          <span
            className={cn(
              "text-[13px] font-[var(--font-body)] font-medium whitespace-nowrap opacity-100 transition-opacity duration-200 ease-out",
              isActive
                ? "text-[var(--content-inverted)]"
                : "text-[var(--content-primary)]",
            )}
          >
            {item.label}
          </span>
        )}
      </button>
      {/* Tooltip on hover when collapsed — fixed position to escape overflow */}
      {!expanded &&
        hovered &&
        (() => {
          const pos = getTooltipPos();
          return (
            <div
              style={{
                position: "fixed",
                left: pos.left,
                top: pos.top,
                transform: "translateY(-50%)",
              }}
              className="pointer-events-none z-[9999] rounded-lg bg-[#111827] px-3 py-[5px] text-xs font-[var(--font-body)] font-semibold whitespace-nowrap text-white shadow-[0_4px_12px_rgba(0,0,0,0.15)]"
            >
              {item.label}
            </div>
          );
        })()}
    </div>
  );
}

/* ── Main sidebar component ── */
export default function IconSidebar({
  activeTab,
  onTabChange,
  expanded,
}: Props) {
  const { logout, user } = useClarityAuth();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const displayName = getUserDisplayName(user ?? {});
  const displayEmail = user?.email ?? "Authenticated ClarityOS user";
  const displayInitials = getUserInitials(user ?? {});
  const shouldShowSecondaryEmail = displayEmail !== displayName;

  // Close profile menu on outside click
  useEffect(() => {
    if (!profileMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [profileMenuOpen]);

  return (
    <aside
      className={cn(
        "flex h-screen shrink-0 flex-col overflow-x-hidden overflow-y-auto border-r border-[var(--border-subtle)] bg-[var(--surface-page)] transition-[width] duration-200 ease-out",
        expanded ? "w-[240px] items-stretch" : "w-[72px] items-center",
      )}
    >
      {/* ── Logo area — matches TopBar 64px height ── */}
      <div
        className={cn(
          "flex h-16 shrink-0 items-center pt-2 transition-[padding-left] duration-200 ease-out",
          expanded ? "justify-start pl-3" : "justify-center pl-0",
        )}
      >
        <div className="flex h-[26px] items-center">
          {expanded ? <ClarityFullLogo /> : <ClarityLogomark />}
        </div>
      </div>

      {/* ── Divider below logo ── */}
      <div className="flex justify-center px-4">
        <div
          className={cn(
            "h-px bg-[var(--border-subtle)] transition-[width] duration-200 ease-out",
            expanded ? "w-full" : "w-10",
          )}
        />
      </div>

      {/* ── Nav items ── */}
      <div
        className={cn(
          "mt-5 flex flex-col gap-1 transition-[padding] duration-200 ease-out",
          expanded ? "items-stretch px-3" : "items-center px-0",
        )}
      >
        {NAV_ITEMS.map((item) => (
          <NavButton
            key={item.tab}
            item={item}
            isActive={activeTab === item.tab}
            onClick={() => onTabChange(item.tab)}
            expanded={expanded}
          />
        ))}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Spacer before settings */}

      {/* ── Settings + Profile ── */}
      <div
        className={cn(
          "flex w-full shrink-0 flex-col pb-4 transition-[padding] duration-200 ease-out",
          expanded ? "items-stretch px-3" : "items-center px-0",
        )}
      >
        {/* Divider above profile */}
        <div
          className={cn(
            "mb-3 flex w-full justify-center",
            expanded ? "px-1" : "px-4",
          )}
        >
          <div
            className={cn(
              "h-px bg-[var(--border-default)] transition-[width] duration-200 ease-out",
              expanded ? "w-full" : "w-10",
            )}
          />
        </div>

        {/* Profile row */}
        <div ref={profileMenuRef} className="relative w-full">
          <button
            aria-label={`${displayName} account menu`}
            onClick={() => setProfileMenuOpen((v) => !v)}
            className={cn(
              "flex cursor-pointer items-center gap-2.5 rounded-[10px] border-0 transition-colors duration-150 hover:bg-[var(--surface-muted)]",
              expanded
                ? "w-full justify-start px-2 py-1.5"
                : "mx-auto size-9 justify-center p-0",
              profileMenuOpen ? "bg-[var(--surface-muted)]" : "bg-transparent",
            )}
          >
            <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-full)] bg-[var(--surface-muted)]">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                <span className="text-xs font-[var(--font-body)] font-semibold text-[var(--content-secondary)]">
                  {displayInitials}
                </span>
              )}
            </div>
            {expanded && (
              <>
                <span className="flex-1 text-left text-[13px] font-[var(--font-body)] font-medium whitespace-nowrap text-[var(--content-primary)]">
                  {displayName}
                </span>
                <ChevronUp
                  className={cn(
                    "size-3.5 shrink-0 text-[var(--content-tertiary)] transition-transform duration-200",
                    profileMenuOpen ? "rotate-0" : "rotate-180",
                  )}
                  strokeWidth={2}
                />
              </>
            )}
          </button>

          {/* Logout popover */}
          {profileMenuOpen && (
            <div
              className={cn(
                "animate-profile-menu-in absolute bottom-[calc(100%+8px)] z-[100] rounded-xl border border-[var(--border-subtle)] bg-white p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.03)]",
                expanded ? "left-0 w-full" : "left-[-8px] w-[180px]",
              )}
            >
              <div className="mb-1 border-b border-[var(--border-subtle)] px-3 py-2.5">
                <p className="m-0 text-[13px] leading-[1.3] font-[var(--font-body)] font-semibold text-[var(--content-primary)]">
                  {displayName}
                </p>
                {shouldShowSecondaryEmail ? (
                  <p className="m-0 mt-0.5 text-xs font-[var(--font-body)] font-normal text-[var(--content-tertiary)]">
                    {displayEmail}
                  </p>
                ) : null}
              </div>
              <button
                onClick={() => {
                  setProfileMenuOpen(false);
                  void logout({
                    logoutParams: {
                      returnTo: window.location.origin,
                    },
                  });
                }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-0 bg-transparent px-3 py-[9px] text-[13px] font-[var(--font-body)] font-medium text-[#DC2626] transition-colors duration-100 hover:bg-[#FEF2F2]"
              >
                <LogOut className="size-[15px]" strokeWidth={1.8} />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
